import { useState } from 'react';
import { Bell, Envelope, Megaphone } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useLoaderData, useRevalidator } from 'react-router-dom';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card';
import { notificationsAPI } from '../../data/notifications';
import pt from '../../locales/pt';

function ToggleRow({ icon: Icon, title, description, checked, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-4 last:border-0">
      <div className="flex gap-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-sage-600" aria-hidden />
        <div>
          <p className="font-medium text-foreground">{title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <label className="relative inline-flex cursor-pointer items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="peer h-6 w-11 rounded-full bg-stone-300 dark:bg-stone-600 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-sage-600 peer-checked:after:translate-x-full peer-disabled:opacity-50" />
      </label>
    </div>
  );
}

export function Notifications() {
  const profile = useLoaderData();
  const { revalidate } = useRevalidator();
  const [saving, setSaving] = useState(false);

  const onToggleProduct = async (field, value) => {
    setSaving(true);
    const res = await notificationsAPI.updatePreferences({ [field]: value });
    setSaving(false);
    if (res.success) {
      toast.success(pt.settings.notifications.saved);
      revalidate();
    } else {
      toast.error(res.error);
    }
  };

  const onToggleMarketing = async (value) => {
    setSaving(true);
    const res = value
      ? await notificationsAPI.marketingOptIn('settings')
      : await notificationsAPI.marketingOptOut();
    setSaving(false);
    if (res.success) {
      toast.success(pt.settings.notifications.saved);
      revalidate();
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 md:p-8">
      <div className="hidden md:block">
        <h1 className="text-2xl font-semibold text-foreground">{pt.settings.notifications.title}</h1>
        <p className="mt-1 text-muted-foreground">{pt.settings.notifications.subtitle}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{pt.settings.notifications.productTitle}</CardTitle>
          <CardDescription>{pt.settings.notifications.productDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          <ToggleRow
            icon={Envelope}
            title={pt.settings.notifications.emailAlertsTitle}
            description={pt.settings.notifications.emailAlertsDesc}
            checked={profile.email_notifications_enabled ?? true}
            disabled={saving}
            onChange={(v) => onToggleProduct('email_notifications_enabled', v)}
          />
          <ToggleRow
            icon={Bell}
            title={pt.settings.notifications.productUpdatesTitle}
            description={pt.settings.notifications.productUpdatesDesc}
            checked={profile.product_updates_enabled ?? true}
            disabled={saving || !(profile.email_notifications_enabled ?? true)}
            onChange={(v) => onToggleProduct('product_updates_enabled', v)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{pt.settings.notifications.marketingTitle}</CardTitle>
          <CardDescription>{pt.settings.notifications.marketingDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          <ToggleRow
            icon={Megaphone}
            title={pt.settings.notifications.marketingToggleTitle}
            description={pt.settings.notifications.marketingToggleDesc}
            checked={profile.marketing_emails_enabled ?? false}
            disabled={saving}
            onChange={onToggleMarketing}
          />
          <p className="mt-4 text-xs text-muted-foreground">{pt.settings.notifications.marketingLegal}</p>
        </CardContent>
      </Card>
    </div>
  );
}

Notifications.loader = async () => {
  const { notificationsLoader } = await import('../../data/notifications');
  return notificationsLoader();
};
